/**
 * The rule that decides whether a sheet may be approved (F4, D18).
 *
 * Pure, and in its own file, because it is the only logic in this app that can
 * cost a sheet of canvas.
 *
 * At 145 pixels per inch a stitch is eight pixels, so the entire error budget
 * is under three of them, and at fit-to-screen a perfect sheet and a badly
 * slipped one look identical: the zoom crops are the only place alignment is
 * visible. Approval used to wait until every one had been opened. Since
 * 2026-10-02 it does not -- the constraint was removed on request -- so the
 * count of views opened is shown as information, and which ones were opened is
 * still recorded with the approval.
 */
import { stateLabel, type SheetState } from './model';

export interface ReviewState {
  /** Every crop the solver produced for this sheet, in display order. */
  readonly crops: readonly string[];
  /** Crops the reviewer has actually opened. */
  readonly opened: ReadonlySet<string>;
}

export interface ReviewProgress {
  total: number;
  opened: number;
  remaining: string[];
  canApprove: boolean;
  /** What to put on, or next to, the approve button. */
  hint: string;
}

export function reviewProgress(state: ReviewState): ReviewProgress {
  const remaining = state.crops.filter((name) => !state.opened.has(name));
  const opened = state.crops.length - remaining.length;

  // A sheet with no crops cannot be approved. That is not a technicality: it
  // means the solver produced no overlays, so there is nothing that could be
  // examined, and approving would be a click with no evidence behind it.
  const canApprove = state.crops.length > 0;

  let hint: string;
  if (state.crops.length === 0) {
    hint = 'No zoom crops were produced for this sheet, so there is nothing to '
      + 'check. It cannot be approved.';
  } else if (remaining.length === 0) {
    hint = `All ${state.crops.length} views opened.`;
  } else {
    hint = `${opened} of ${state.crops.length} views opened.`;
  }
  return { total: state.crops.length, opened, remaining, canApprove, hint };
}

/**
 * The views counted on the review screen, from the names the solver produced.
 *
 * Every one of them, in display order, `overlay_full` included: the whole-sheet
 * view has its own panel rather than a tile in the crop grid, but it is still
 * one of the views the count is out of.
 */
export function approvalCrops(overlayNames: readonly string[]): string[] {
  return orderCrops(overlayNames);
}

/**
 * Order the crops so the ones that matter most are hardest to skip.
 *
 * The solver names them for where it took them: the nine fixed probes, the
 * worst-residual spots, and the densest ink. The worst and the inked ones are
 * where a slip shows first, so they come first -- someone clicking through
 * without looking still sees those two before their attention runs out.
 */
export function orderCrops(names: readonly string[]): string[] {
  const rank = (name: string): number => {
    if (name.startsWith('overlay_zoom_worst')) return 0;
    if (name.startsWith('overlay_zoom_ink')) return 1;
    if (name === 'overlay_full') return 3;
    return 2;
  };
  return [...names].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

/** `overlay_zoom_worst1` -> `Worst residual 1`, for a caption. */
export function cropLabel(name: string): string {
  const zoom = name.replace(/^overlay_zoom_/, '');
  if (zoom === 'overlay_full') return 'Whole sheet';
  const worst = /^worst(\d+)$/.exec(zoom);
  if (worst) return `Worst residual ${worst[1]}`;
  const ink = /^ink(\d+)$/.exec(zoom);
  if (ink) return `Densest ink ${ink[1]}`;
  return {
    tl: 'Top left', tr: 'Top right', bl: 'Bottom left', br: 'Bottom right',
    top: 'Top edge', bottom: 'Bottom edge', left: 'Left edge',
    right: 'Right edge', centre: 'Centre',
  }[zoom] ?? zoom;
}

/**
 * Has the solver settled which way up this sheet prints (D8)?
 *
 * Deliberately `=== true` rather than truthy. The three ways this is not a
 * decision -- the report has not loaded, the run predates the field, the
 * printer has no survey -- must all read as "not settled", because the screen
 * that says orientation was checked when it was not is worse than the one that
 * asks for a second look it did not need. A sheet printed upside down is scrap:
 * the designs carry text.
 */
export function orientationSettled(
  report: { orientation_chosen?: { settled_in_page_coordinates?: boolean } }
    | null | undefined,
): boolean {
  return report?.orientation_chosen?.settled_in_page_coordinates === true;
}

/**
 * Whether the print file can be downloaded, and if not, why not.
 *
 * `approved` only, because that is the only state `api/src/download-pdf.ts`
 * signs a URL for; offering it anywhere else is a button that fails.
 */
export function downloadState(state: SheetState): { enabled: boolean; note: string | null } {
  if (state === 'approved') return { enabled: true, note: null };
  if (state === 'needs_review') {
    return { enabled: false,
             note: 'Available once the sheet is approved.' };
  }
  return { enabled: false, note: `No print file: ${stateLabel(state)}.` };
}
